import _ from "lodash";
import { Sentry } from "services/sentry";
import { generateRandomId } from "Utils";

import { KONVA_ATTR } from "./KonvaConstants";
import { createTextEditingField } from "./KonvaElements";
import { getStageProps, getTransformerProps } from "./KonvaElementProps";
import { addElementsToCanvas, getKonvaQuestionType } from "./KonvaUtils";
import {
  addDragAndDropQuestionEventListener,
  showSolutionForSubmittedQuestion,
  addFindHotspotQuestionEventListener,
  updateFindHotspotElementsAfterDrawing,
  addLabellingQuestionEventListener,
  showLabellingEvaluationView,
} from "../KonvaQuestionType";

let konva;
let transformer = null;

/**
 *
 * @param {*} payload
 * @param {*} konvaFields
 * @returns Konva Static fields for each question
 */
const getKonvaInitialFields = async (payload, konvaFields) => {
  if (!konva) {
    konva = await import(/* webpackChunkName: "konva" */ "konva");
  }

  let stage = _.get(konvaFields, "current.stage");
  let layer = _.get(konvaFields, "current.layer");
  let virtualLayer = _.get(konvaFields, "current.virtualLayer");

  if (!stage || !layer || !virtualLayer) {
    const stageProps = getStageProps(_.get(payload, "container"));
    if (!stageProps) return { isError: true };
    stage = new konva.Stage(stageProps);

    layer = new konva.Layer({ id: generateRandomId() });
    virtualLayer = new konva.Layer();

    stage.add(layer);
    stage.add(virtualLayer);
    layer.draw();

    konvaFields.current = { stage, layer, virtualLayer };
  }

  // * destroying and removing last draw children
  if (virtualLayer) {
    virtualLayer.destroyChildren();
    virtualLayer.removeChildren();
  }
  if (layer) {
    layer.destroyChildren();
    layer.removeChildren();
  }

  if (!stage || stage.width() === 0 || stage.height() === 0) {
    return { isError: true };
  }

  return {
    konva,
    stage,
    layer,
    virtualLayer,
  };
};

/**
 *
 * @param {*} container
 * @description : Import konva lazily, create instance of it, add and draw required tools
 * @returns {
 *  layer
 *  transformer
 *  addNewItem
 *  drawPolygon
 *  resetCanvas
 *  resetLayer
 *  callbacks
 * }
 */
export const createKonvaCanvas = async (payload, konvaFields, t) => {
  if (_.isEmpty(payload, "container")) return null;

  const mode = _.get(payload, "mode");
  const worksheetMode = _.get(payload, "worksheetMode");
  const isCreateMode = mode === "create" && worksheetMode === "teacher_create";
  const questionType = getKonvaQuestionType(_.get(payload, "type"));

  /**
   * can listen only when other than create mode,
   * - if question type is labelling AND
   * - either its edit mode OR preview mode not in teacher create(basically inactive state of question in creation)
   */
  const canAlsoListenOn =
    questionType.isLabelling &&
    (mode === "edit" ||
      (mode === "preview" && worksheetMode !== "teacher_create"));

  try {
    const {
      konva,
      stage,
      layer,
      virtualLayer,
      isError,
    } = await getKonvaInitialFields(payload, konvaFields);
    if (isError) return null;

    // * Adding stage, layer listening property based on requirements
    if (
      !_.get(payload, "options.allowInteraction") &&
      mode === "preview" &&
      !canAlsoListenOn
    ) {
      // * setting canvas idle on preview mode to improve performance
      stage.listening(false);
      layer.listening(false);
      virtualLayer.listening(false);
    } else if (isCreateMode || canAlsoListenOn) {
      stage.listening(true);
      layer.listening(true);
      virtualLayer.listening(true);

      if (transformer) {
        // * Reset transformer
        transformer.nodes([]);
        transformer.detach();
        transformer.destroy();
        transformer = null;
      }

      const transformerProps = getTransformerProps();
      transformer = new konva.Transformer(transformerProps);
      const isDrawing = _.get(payload, "drawMode.isDrawing", false);
      stage.setAttr(
        KONVA_ATTR.IS_DRAWING_POLYGON,
        _.get(payload, "drawMode.type")
      );
      transformer.visible(!isDrawing);
    }

    const instance = {
      konva,
      stage,
      layer,
      transformer,
      virtualLayer,
    };

    const konvaInstance = {
      ...instance,

      // * callbacks
      drawMode: _.get(payload, "drawMode"),
      options: _.get(payload, "options", {}),
      onDrop: _.get(payload, "onDrop", () => {}),
      onClick: _.get(payload, "onClick", () => {}),
      onDragEnd: _.get(payload, "onDragEnd", () => {}),
      onTransform: _.get(payload, "onTransform", () => {}),
      onDrawCancel: _.get(payload, "onDrawCancel", () => {}),
      onTextChange: _.get(payload, "onTextChange", () => {}),
      onDrawComplete: _.get(payload, "onDrawComplete", () => {}),
    };

    // * Adding callbacks for canvas events
    if (questionType.isDragAndDrop) {
      // * Drag and drop question type event listener
      addDragAndDropQuestionEventListener(konvaInstance, mode, () => {
        if (isCreateMode) createTextEditingField(konvaInstance, t, mode);
      });
    } else if (questionType.isFindHotspot) {
      // * Find hotspot question type event listener
      addFindHotspotQuestionEventListener(konvaInstance, mode, () => {
        if (isCreateMode) createTextEditingField(konvaInstance, t, mode);
      });
    } else if (questionType.isLabelling) {
      addLabellingQuestionEventListener(konvaInstance, mode, () => {
        if (mode !== "view") createTextEditingField(konvaInstance, t, mode);
      });
    } else {
      // * Add for other question types
    }

    return {
      ...instance,
      addElements: items => {
        try {
          addElementsToCanvas(
            items,
            {
              mode,
              questionType,
              stage,
              layer,
              transformer,
              konva,
            },
            t
          );

          // * Action post element added to canvas
          if (questionType.isDragAndDrop) {
            // * Drag and drop question action after adding elements
            showSolutionForSubmittedQuestion(mode, {
              ...instance,
              options: _.get(payload, "options", {}),
            });
          } else if (questionType.isFindHotspot) {
            // * show correct/wrong marker's on canvas
            updateFindHotspotElementsAfterDrawing(mode, {
              ...instance,
              options: _.get(payload, "options", {}),
            });
          } else if (questionType.isLabelling) {
            // * show correct/wrong label's on canvas
            showLabellingEvaluationView(mode, items, {
              ...instance,
              options: _.get(payload, "options", {}),
            });
          }
        } catch (error) {
          Sentry.captureMessage(`KonvaError: ${error.message}`, {
            extra: { mode, questionType: _.get(payload, "type"), items },
          });
        }
      },
    };
  } catch (error) {
    Sentry.captureMessage(`KonvaError: ${error.message}`, {
      extra: {
        mode: _.get(payload, "mode"),
        questionType: _.get(payload, "type"),
      },
    });
    return null;
  }
};
